import { useCallback, useEffect, useRef, useState } from 'react';
import {
  apiCasos,
  apiCasosArvores,
  apiCasosAtivos,
  apiCasosSuspeitas,
  apiInvestigados,
  QUALIFICACOES,
  apiRelatorios,
  ErroDaApi,
  type Caso,
  type CategoriaDeAtivo,
  type EmpresaDoMapa,
  type LinhaDoQuadroDeAtivos,
  type NivelServico,
  type PesquisaRegistravel,
  type ApuracaoDoTopico,
  type Relatorio,
  type SecaoDoRelatorio,
  type SuspeitaDeUm,
} from '../lib/api.ts';
import { ApuracaoDoRelatorio } from './ApuracaoDoRelatorio.tsx';
import { usarEhMaster } from '../lib/papel.ts';
import { usarSigilo } from '../lib/sigilo.ts';
import { MarcosDaDivida } from './MarcosDaDivida.tsx';
import { Secao } from './Secao.tsx';
import { AberturaDoRelatorio } from './AberturaDoRelatorio.tsx';
import { PassivoDoCaso } from './PassivoDoCaso.tsx';
import { IndiceDeCobertura } from './AtivosLocalizados';
import { VeiculosDoCaso } from './VeiculosDoCaso';
import { OutrosDoCaso } from './OutrosDoCaso';
import { AtosDoCaso } from './AtosDoCaso';
import { ProcessosDoCaso } from './ProcessosDoCaso';
import { QualificacaoDoCaso } from './QualificacaoDoCaso.tsx';
import { PainelDeInvestigados } from './Investigados.tsx';
import { Anotacoes } from './Anotacoes.tsx';
import { MenuDoRelatorio } from './MenuDoRelatorio';
import { ContratosSociais } from './ContratosSociais.tsx';
import { SumarioDoRelatorio } from './SumarioDoRelatorio.tsx';
import { LinhaDoTempo } from './LinhaDoTempo.tsx';
import { Valor } from './Valor.tsx';
import { avisar, avisarErro } from './Avisos.tsx';
import { paraTela } from '../lib/datas';
import { usarDefinirTrilha } from '../lib/trilha.ts';
import { avancoDoRelatorio } from '../lib/avanco-do-relatorio.ts';
import { PilulaDoFundo } from './PilulaDoFundo.tsx';
import { Icone } from './Icone';
import { PesquisaFeitaFora } from './PesquisaFeitaFora.tsx';
import { EmailDeEntrega } from './EmailDeEntrega.tsx';
import { ImoveisDoCaso, iniciais } from './ImoveisDoCaso.tsx';
import { SociosNoExterior } from './SociosNoExterior.tsx';
import { SinaisDeBlindagem } from './SinaisDeBlindagem.tsx';
import { IndiceDeRecuperacao } from './IndiceDeRecuperacao.tsx';
import '../estilos/sistema.css';
import '../estilos/relatorio-do-caso.css';

/**
 * Em que capítulo cada pesquisa feita fora do Hub se registra — o mesmo onde a
 * ressalva dela imprime (`RESSALVA[servico].secoes`, na API).
 */
const PESQUISAS_DO_CAPITULO: Record<string, PesquisaRegistravel[]> = {
  /* ⭐ As duas pesquisas dos imóveis (por titular e indisponibilidade, 14/09)
     moraram aqui até 16/09/2026: passaram para DENTRO do tópico, na faixa da
     parte escolhida (`ImoveisDoCaso`). Listá-las aqui também daria dois
     lugares para o mesmo registro. */
  societaria: ['junta_comercial'],
  /* ⭐ 16/10/2026 (PRD §52.9): *"No tópico veículos, pesquisa embarcações também."* Não há
     consulta automática pelo dono; a pesquisa feita fora se registra aqui, parte por parte, e é
     o que deixa o tópico afirmar a negativa. */
  'ativos-veiculos': ['embarcacoes'],
  /* ⭐ As marcas moraram aqui, em "Outros ativos", de 18/09/2026 até o tópico
     ganhar o desenho de Imóveis (no mesmo dia): o registro passou para DENTRO
     do tópico, na faixa da parte escolhida (`OutrosDoCaso`). Listá-lo aqui
     também daria dois lugares para o mesmo registro. */
};

/**
 * Escrever o relatório, seção por seção.
 *
 * ⚠️ **Grava seção a seção, não o documento inteiro.** Um relatório do N3 tem
 * dezesseis seções e horas de escrita; um "Salvar" único no fim significaria
 * perder tudo se a sessão cair. Cada seção grava sozinha ao sair do campo — e a
 * tela diz qual foi gravada, porque gravação silenciosa não tranquiliza ninguém.
 *
 * ⚠️ **Entregue não se edita**, e os campos aparecem travados em vez de sumir:
 * o documento continua legível, que é o que se quer de um registro do que o
 * cliente recebeu.
 */
/**
 * O que o Hub tem a apontar sobre possível interposição de pessoa.
 *
 * ⚠️ **Nunca diz "laranja".** Ver o comentário no ponto em que é usado.
 */
function PainelDeSuspeitas({ casoId }: { casoId: string }) {
  const [dados, setDados] = useState<{
    suspeitas: SuspeitaDeUm[];
    lacunas: string[];
  } | null>(null);

  useEffect(() => {
    void apiCasosSuspeitas
      .ler(casoId)
      .then(setDados)
      .catch(() => setDados(null));
  }, [casoId]);

  if (!dados || (dados.suspeitas.length === 0 && dados.lacunas.length === 0)) return null;

  return (
    <div className="bloco">
      <h2 className="titulo-secao">Sinais que merecem olhar</h2>
      <p className="ajuda ajuda-solta">
        O que o Hub encontrou cruzando datas, parentesco e endereço.{' '}
        <strong>Nenhum sinal isolado conclui nada</strong> — os tribunais exigem a
        conjugação de elementos, e quem conjuga é quem assina. Isto não sai no
        relatório.
      </p>

      {/* ⚠️ A lacuna vem ANTES dos sinais: sem ela, quatro achados parecem a
          análise inteira quando na verdade três nem foram avaliados. */}
      {dados.lacunas.map((l) => (
        <p key={l} className="ajuda-alerta ajuda-solta">
          {l}
        </p>
      ))}

      {dados.suspeitas.map((s) => (
        <div key={s.conexaoId} className="suspeita">
          <p>
            <strong>{s.nome}</strong>
            {s.documento && <span className="dado"> · {s.documento}</span>}{' '}
            <span className="etiqueta etiqueta-vinculo">{s.relacao}</span>
          </p>
          <ul>
            {s.sinais.map((x) => (
              <li key={x.chave} className={x.peso === 'forte' ? 'sinal-forte' : 'sinal-medio'}>
                {x.texto}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * O PAINEL DE ATIVOS — o mesmo desenho que abre a Parte II do documento.
 *
 * ⚠️ **Não é uma segunda versão do painel: é o painel.** A rota devolve o SVG
 * que `resumoEmSvg` monta, e é dele que o `.docx` tira o PNG. Redesenhar aqui
 * seria abrir a terceira divergência da série (`docs/PRD.md` §21) — a primeira
 * foram dois geradores, a segunda foram duas rotas alimentando um gerador só.
 *
 * ⚠️ **Com o sigilo ligado, o painel NÃO aparece.** Os valores estão desenhados
 * dentro da imagem, e imagem não se mascara por CSS. Deixá-lo visível seria o
 * defeito que o `CLAUDE.md` descreve: valor escondido em quatro lugares e
 * visível no quinto é pior que não esconder, porque a pessoa confia que está
 * protegida.
 */
function PainelDeAtivos({ casoId, chave }: { casoId: string; chave: string }) {
  const oculto = usarSigilo();
  const [existe, setExiste] = useState(true);

  /* Painel que falhou uma vez precisa poder voltar: a ficha muda e o caso passa
     a ter ativo. Sem isto, o primeiro 404 esconderia o painel para sempre. */
  useEffect(() => setExiste(true), [chave]);

  if (oculto) {
    return (
      <p className="ajuda ajuda-solta">
        Painel de ativos oculto enquanto os valores estão fechados — o desenho
        traz o valor apurado dentro dele. Abra o olho no topo para conferir.
      </p>
    );
  }

  if (!existe) return null;

  return (
    <div className="painel-de-ativos">
      <img
        key={chave}
        src={`/api/casos/${casoId}/painel-de-ativos.svg`}
        alt="Ativos localizados por classe de bem, com quantidade e valor documentado"
        onError={() => setExiste(false)}
      />
    </div>
  );
}

/**
 * O MAPA DE VÍNCULOS — o organograma societário, na seção societária.
 *
 * ⚠️ **Nasceu na TELA por reclamação, e é bom lembrar por quê.** O Richard
 * testou o próprio CPF em 18/08/2026 e disse que "não formou um mapa". Estava
 * certo: ele existia só como imagem embutida num documento que era preciso
 * gerar para olhar.
 *
 * ⚠️ **Mudou de lugar em 20/08/2026, e não de existência.** Estava solto no
 * topo da página, antes de tudo; no documento ele fica **dentro da seção
 * societária**, logo abaixo do texto que ilustra. Desenho separado do texto que
 * ele ilustra obriga o leitor a procurar de qual parágrafo ele fala — e, aqui,
 * fazia a tela discordar do documento na única coisa que a auditoria de 20/08
 * existe para alinhar.
 *
 * ⚠️ **É `<img>` de SVG, e não PNG.** O SVG escala sem borrar e o texto dentro
 * dele continua sendo texto. O PNG segue no documento, onde o Word precisa de
 * imagem.
 */
/**
 * ⭐⭐ **DE QUANDO É A BASE DA RECEITA — 10/09/2026.**
 *
 * ⛔ **A rota existia com o comentário "a tela avisa quando ela está velha", e
 * nenhuma tela avisava.** O organograma, o quadro societário e a busca de
 * empresas do sócio saem todos dela — e o Hub afirmava o quadro **sem dizer de
 * quando ele é**. Base de três meses atrás devolve sócio que já saiu e não
 * devolve o que entrou.
 *
 * ⚠️ **Discreto quando está em dia, e alerta quando não está.** Aviso que
 * sempre grita vira paisagem; o corte é **dois meses**, que é o intervalo em
 * que a Receita costuma publicar.
 *
 * ⚠️ **Tela interna.** Isto não sai no documento — nem o mês, nem a existência
 * da base.
 */
function IdadeDaBase() {
  const [mes, setMes] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let vivo = true;
    apiInvestigados
      .baseDaReceita()
      .then((r) => vivo && setMes(r.mes))
      .catch(() => vivo && setMes(null));
    return () => {
      vivo = false;
    };
  }, []);

  if (mes === undefined) return null;

  /* ⭐ No desenho de Imóveis desde 19/09/2026: o alerta na faixa em ouro da casa. */
  if (!mes) {
    return (
      <div className="imv imv-travas imv-travas-solta">
        <p>
          ⚠️ A base da Receita <strong>não respondeu</strong>. O organograma e o quadro
          societário podem estar incompletos, e não dá para dizer de quando são.
        </p>
      </div>
    );
  }

  /* ⚠️ `mes` vem como `AAAA-MM`. A conta é em meses inteiros, e não em dias:
     a Receita publica por mês, e "45 dias" não significaria nada aqui. */
  const [ano, m] = mes.split('-').map(Number);
  const hoje = new Date();
  const meses =
    (hoje.getFullYear() - (ano ?? 0)) * 12 + (hoje.getMonth() + 1 - (m ?? 0));
  const porExtenso = new Date(Date.UTC(ano ?? 0, (m ?? 1) - 1, 1)).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  if (meses <= 2) {
    return (
      <div className="imv imv-legenda imv-legenda-solta">
        <span>Quadro societário conforme a base de {porExtenso}.</span>
      </div>
    );
  }
  return (
    <div className="imv imv-travas imv-travas-solta">
      <p>
        ⚠️ A base societária é de <strong>{porExtenso}</strong> — {meses} meses atrás. Sócio
        que entrou ou saiu depois disso <strong>não aparece aqui</strong>.
      </p>
    </div>
  );
}

function MapaDeVinculos({ casoId, chave }: { casoId: string; chave: string }) {
  const [existe, setExiste] = useState(true);

  /* Mapa que falhou uma vez precisa poder voltar: a ficha muda e o caso passa a
     ter o que ligar. Sem isto, o primeiro 404 o esconderia para sempre. */
  useEffect(() => setExiste(true), [chave]);

  if (!existe) return null;

  return (
    <>
      <div className="mapa-de-vinculos">
        <img
          key={chave}
          src={`/api/casos/${casoId}/mapa.svg`}
          alt="Mapa de vínculos societários e familiares do caso"
          onError={() => setExiste(false)}
        />
      </div>
      {/*
        ⛔ **A LEGENDA SOLTA SAIU EM 23/09/2026, e a falta é deliberada.** O
        organograma de comando traz painel de legenda DENTRO do desenho — com
        amostra do traço, título e descrição de cada elemento. Manter a linha
        de fora explicaria pela metade (só o tracejado dourado) e contradiria
        o painel, que explica os sete.
      */}
      <EmpresasDoMapa casoId={casoId} chave={chave} />
    </>
  );
}

/**
 * ⭐ A TABELA DAS EMPRESAS DO MAPA — 27/10/2026, aprovado por ele.
 *
 * De 5 empresas para cima o mapa sai em grade e o documento traz, logo depois, a tabela de todas as empresas. A
 * tela mostra a mesma, com as mesmas colunas e na mesma ordem (a trava do PRD §21). No caso pequeno a lista vem
 * vazia e nada aparece.
 */
function EmpresasDoMapa({ casoId, chave }: { casoId: string; chave: string }) {
  const [linhas, setLinhas] = useState<EmpresaDoMapa[]>([]);

  useEffect(() => {
    void apiCasosAtivos
      .empresasDoMapa(casoId)
      .then((d) => setLinhas(d.empresas))
      .catch(() => setLinhas([]));
  }, [casoId, chave]);

  if (linhas.length === 0) return null;

  return (
    <section className="imv-quadro empresas-do-mapa">
      <header className="imv-quadro-cab">
        <span className="imv-rot">Empresas do quadro ({linhas.length})</span>
        <small>Todas as empresas do mapa, na ordem dos blocos. As etiquetas são as mesmas dos cartões.</small>
      </header>
      <div className="imv-quadro-rola">
        <table>
          <thead>
            <tr>
              <th scope="col">Empresa</th>
              <th scope="col">CNPJ</th>
              <th scope="col">Administração</th>
              <th scope="col">Quota</th>
              <th scope="col">Situação</th>
              <th scope="col">Etiquetas</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.cnpj + l.empresa}>
                <th scope="row">
                  {l.empresa}
                  {l.devedora && <strong className="empresas-do-mapa-devedora"> (devedora)</strong>}
                </th>
                <td className="dado empresas-do-mapa-cnpj">{l.cnpj}</td>
                <td>
                  {l.administracao}
                  {l.desde && <span className="dado empresas-do-mapa-desde">desde {l.desde}</span>}
                </td>
                <td>{l.quota || '—'}</td>
                <td>{l.situacao}</td>
                <td className="empresas-do-mapa-etiquetas">{l.etiquetas}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * AS ÁRVORES GENEALÓGICAS — uma por investigado, em "Relações familiares
 * relevantes".
 *
 * ⛔ **Ficaram ÓRFÃS de 20 a 28/08/2026.** O componente existia inteiro e
 * **nenhuma linha o chamava**; no documento a âncora era a chave `vinculos`,
 * que **nunca existiu**. Interruptor ligado contra chave inexistente não dá
 * erro — não mostra nada, em silêncio. Ganharam casa quando o Richard mandou
 * construir o SUMÁRIO EXECUTIVO da estrutura que entregou em 28/08.
 *
 * ⚠️ **Faltavam na tela até 20/08/2026**, e é o mesmo defeito corrigido duas
 * vezes no mesmo dia: peça que vai ao cliente e que não se revisa antes de
 * gerar. O documento imprime uma por parte investigada; a tela não mostrava
 * nenhuma.
 *
 * ⚠️ **É o MESMO desenho do documento** — a rota devolve o SVG de que o `.docx`
 * tira o PNG, e a regra de quem tem árvore vive só no servidor
 * (`lib/arvores-do-caso.ts`).
 *
 * ⚠️ **Uma por pessoa, e não um mapa com todos.** O desenho único foi tentado
 * em 19/08/2026 e o Richard não conseguiu ler: *"não dá pra saber de quem é
 * quem"*.
 */
function ArvoresGenealogicas({ casoId, chave }: { casoId: string; chave: string }) {
  const [arvores, setArvores] = useState<Array<{ investigadoId: string; nome: string }>>([]);
  const [qualificacoes, setQualificacoes] = useState<Record<string, string>>({});
  const [escolhida, setEscolhida] = useState<string | null>(null);

  useEffect(() => {
    void apiCasosArvores
      .listar(casoId)
      .then((d) => setArvores(d.arvores))
      .catch(() => setArvores([]));
    /* Só para o rótulo da barra — a classe de cada parte. */
    void apiInvestigados
      .listar(casoId)
      .then((d) => setQualificacoes(Object.fromEntries(d.investigados.map((i) => [i.id, QUALIFICACOES[i.qualificacao]]))))
      .catch(() => setQualificacoes({}));
  }, [casoId, chave]);

  /* Sem parente cadastrado o documento não imprime árvore nenhuma — e a tela
     também não desenha. Bloco vazio sugeriria que faltou carregar. */
  if (arvores.length === 0) return null;
  const a = arvores.find((x) => x.investigadoId === escolhida) ?? arvores[0]!;

  /*
   * ⭐ **No desenho de Imóveis desde 19/09/2026 (PRD §46.10): uma árvore por
   * vez, pela barra das partes.** Empilhadas, com as partes de um caso real, a
   * página não acabava. O documento continua imprimindo todas.
   */
  return (
    <div className="imv">
      <div className="imv-pessoas" role="tablist" aria-label="De quem é a árvore">
        {arvores.map((x) => (
          <button
            key={x.investigadoId}
            type="button"
            role="tab"
            aria-selected={x.investigadoId === a.investigadoId}
            className={x.investigadoId === a.investigadoId ? 'ativo' : undefined}
            onClick={() => setEscolhida(x.investigadoId)}
          >
            <span className="imv-av">{iniciais(x.nome)}</span>
            <span className="imv-txt">
              <b>{x.nome}</b>
              <small>{qualificacoes[x.investigadoId] ?? 'árvore genealógica'}</small>
            </span>
          </button>
        ))}
      </div>
      {/*
        ⚠️ **Sem título por fora, e é de propósito:** o desenho já traz
        "ÁRVORE GENEALÓGICA · FULANO" dentro dele, como no documento. Um
        título repetido na tela seria hierarquia que o documento não tem.

        ⚠️ **Na tela ela é maior que no papel, e isto é escolha.** O
        documento reduz a árvore para caber na folha; a tela tem rolagem e
        não precisa reduzir — e é aqui que o nome e o grau são conferidos
        antes de virar documento assinado.
      */}
      <div className="arvore-genealogica">
        <img
          key={`${a.investigadoId}-${chave}`}
          src={`/api/investigados/${a.investigadoId}/arvore.svg`}
          alt={`Árvore genealógica de ${a.nome}`}
        />
      </div>
      {/* ⚠️ A mesma frase do documento, palavra por palavra. */}
      <div className="imv-legenda">
        <span className="imv-legenda-longa">
          <span className="imv-info">i</span> Uma árvore por parte investigada. O fio tracejado e a interrogação
          marcam vínculo apontado e ainda não confirmado por documento.
        </span>
      </div>
    </div>
  );
}

/**
 * O QUADRO DE ATIVOS POR QUALIFICAÇÃO — a matriz que fecha a Parte I.
 *
 * ⚠️ **Vem em JSON e é desenhado aqui, e não como imagem, porque quadro é
 * DADO.** Na tela ele precisa acompanhar o tema escuro, ser lido por leitor de
 * tela e ter o valor passando pelo componente `Valor`. O que garante a
 * igualdade com o Word não é o formato: é `quadroPorQualificacao` ser a única a
 * montar as linhas e `linhasImpressasDoQuadro` a única a decidir quais saem.
 *
 * ⚠️ **A pergunta que ele responde é "de quem é o quê"** — a que o painel de
 * ícones não responde. Num caso com devedor, avalista e três parentes, saber
 * que há 14 ativos não orienta ninguém; saber que 11 estão com os parentes
 * orienta o caso inteiro (`docs/PRD.md` §19.1).
 */
function QuadroPorQualificacao({
  casoId,
  nivel,
  chave,
}: {
  casoId: string;
  nivel: NivelServico;
  chave: string;
}) {
  const [linhas, setLinhas] = useState<LinhaDoQuadroDeAtivos[]>([]);
  const [ressalva, setRessalva] = useState<string | null>(null);
  const [colunas, setColunas] = useState<Array<{ chave: CategoriaDeAtivo; titulo: string }>>([]);

  useEffect(() => {
    void apiCasosAtivos
      .quadro(casoId)
      .then((d) => {
        setLinhas(d.linhas);
        setRessalva(d.ressalva);
        setColunas(d.colunas);
      })
      .catch(() => {
        setLinhas([]);
        setRessalva(null);
        setColunas([]);
      });
  }, [casoId, chave]);

  /* Sem investigado na ficha o documento não imprime quadro nenhum — e a tela
     também não desenha. Quadro vazio sugeriria que faltou carregar. */
  if (linhas.length === 0) return null;

  /** Nome + valor + uma coluna por categoria — a largura do `colSpan` do grupo. */
  const largura = 2 + colunas.length;

  /* ⚠️ Zero sai como travessão, e não como `0`: o documento faz o mesmo, e a
     coluna de números fica legível de relance. */
  const numero = (n: number) => (n > 0 ? String(n) : '—');

  /* ⭐ No desenho de Imóveis desde 19/09/2026 (PRD §46.8): a tabela da casa, com o
     título em rótulo — o conteúdo, a ordem e as frases são os de antes. */
  return (
    <section className="imv-quadro">
      <header className="imv-quadro-cab">
        <span className="imv-rot">Quadro de ativos por qualificação</span>
        {/* ⚠️ **A mesma frase do documento, palavra por palavra.** Duas redações
            para a mesma tabela são duas explicações que um dia se contradizem. */}
        <small>
          Ativos localizados por parte investigada, agrupados pela qualificação
          dela no caso. O valor apurado soma apenas o que tem valor documentado e
          integra o patrimônio atual.
        </small>
      </header>
      <div className={`quadro-de-ativos imv-quadro-rola nivel-${nivel}`}>
        <table>
          <thead>
            <tr>
              {/* ⚠️ No Word esta célula é vazia; aqui ela tem nome porque
                  tabela sem cabeçalho de coluna não se lê em leitor de tela. */}
              <th scope="col">Parte investigada</th>
              <th scope="col" className="quadro-numero">
                Valor apurado
              </th>
              {/* ⚠️ **Os títulos vêm do servidor**, na mesma ordem em que o
                  documento os imprime. Escritos aqui, seriam a lista que
                  envelhece — foi assim que "Partic. societária" continuou na
                  tela apontando para dado que mudara de lugar em 21/08. */}
              {colunas.map((c) => (
                <th key={c.chave} scope="col" className="quadro-numero">
                  {c.titulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) =>
              l.especie === 'grupo' ? (
                <tr key={`${l.especie}-${i}`} className="quadro-grupo">
                  {/* A classe encabeça as linhas que vêm abaixo dela, não as
                      colunas — daí `rowgroup`. No Word ela ocupa a primeira
                      célula e deixa as outras quatro vazias; aqui atravessa a
                      linha inteira, que é como uma tabela de tela se lê. */}
                  <th scope="rowgroup" colSpan={largura}>
                    {l.rotulo}
                  </th>
                </tr>
              ) : (
                <tr
                  key={`${l.especie}-${i}`}
                  className={l.especie === 'total' ? 'quadro-total' : undefined}
                >
                  <th scope="row">{l.rotulo}</th>
                  <td className="quadro-numero">
                    {/*
                      ⚠️ **Passa pelo `Valor` como todo dinheiro da casa** — mas
                      só quando existe. Nulo aqui quer dizer "não há valor
                      documentado", e o `Valor` usaria o mesmo `R$ ••••` que usa
                      para "este valor não é seu": duas ausências muito
                      diferentes com a mesma cara.
                    */}
                    {l.valor ? <Valor de={l.valor} /> : <span className="dado vazio-do-quadro">—</span>}
                  </td>
                  {colunas.map((c) => (
                    <td key={c.chave} className="quadro-numero dado">
                      {numero(l.contagem[c.chave] ?? 0)}
                    </td>
                  ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {/* ⚠️ **A mesma frase do documento, vinda do mesmo lugar.** Sem ela, três
          imóveis todos transmitidos sairiam como "3 imóveis · —", e o travessão
          seria lido como "não se apurou o valor" quando o certo é "estes bens
          saíram do patrimônio". Só aparece quando há o que ressalvar: frase fixa
          dizendo que nada ficou de fora ensina o olho a pular a linha. */}
      {ressalva && <p className="imv-quadro-pe">{ressalva}</p>}
    </section>
  );
}

/**
 * ⭐⭐ **As duas saídas do relatório — 09/09/2026.**
 *
 * > *"Podemos acrescentar os botões geradores, chamados 'RELATÓRIO' e
 * > 'APRESENTAÇÃO', no topo da página do relatório dos casos."* — Richard
 *
 * ⚠️ **Baixa por `fetch`, e não por `<a href>` como o Fundo de Custas.** A
 * prestação de contas sai em milissegundos; este documento passa pelo
 * LibreOffice e leva segundos. Um link abriria aba em branco sem dizer nada, e
 * a pessoa clicaria de novo — gerando duas vezes. **O botão precisa dizer que
 * está trabalhando.**
 *
 * ⚠️ **Um de cada vez.** Enquanto um gera, o outro fica desabilitado: são duas
 * conversões pesadas no mesmo servidor, e disparar as duas juntas é como se
 * descobre que o LibreOffice briga pelo próprio perfil.
 */
function SaidasDoRelatorio({ relatorioId, codigo }: { relatorioId: string; codigo: string }) {
  const [gerando, setGerando] = useState<'pdf' | 'pptx' | null>(null);
  /* ⭐ 16/10/2026 (PRD §53): o e-mail de entrega, pronto para copiar. Só do master; aparece sempre. */
  const ehMaster = usarEhMaster();
  const [emailAberto, setEmailAberto] = useState(false);

  async function baixar(qual: 'pdf' | 'pptx') {
    if (gerando) return;
    setGerando(qual);
    try {
      const endereco = qual === 'pdf' ? apiRelatorios.saidas.pdf(relatorioId) : apiRelatorios.saidas.pptx(relatorioId);
      const resposta = await fetch(endereco, { credentials: 'same-origin' });
      if (!resposta.ok) {
        /* ⚠️ O corpo do erro é JSON quando a rota recusa; texto quando o
           servidor cai antes. Ler os dois evita "[object Object]" na tela. */
        const bruto = await resposta.text();
        let dito = bruto;
        try {
          dito = (JSON.parse(bruto) as { erro?: string }).erro ?? bruto;
        } catch {
          /* segue com o texto cru */
        }
        throw new Error(dito || `O servidor respondeu ${resposta.status}.`);
      }
      const arquivo = await resposta.blob();
      const url = URL.createObjectURL(arquivo);
      const a = document.createElement('a');
      a.href = url;
      a.download = qual === 'pdf' ? `${codigo}.pdf` : `${codigo}-apresentacao.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      /* ⚠️ Solta a memória do blob — sem isto o arquivo inteiro fica na aba até
         ela fechar, e um relatório com anexos tem megabytes. */
      URL.revokeObjectURL(url);
      avisar(qual === 'pdf' ? 'Relatório gerado.' : 'Apresentação gerada.');
    } catch (e) {
      avisarErro(e instanceof Error ? e.message : 'Não consegui gerar o arquivo.');
    } finally {
      setGerando(null);
    }
  }

  return (
    /* ⭐ 14/09/2026 — sistema visual 2026: o principal (o relatório) fica na
       ponta, com ícone, como toda ação principal do Hub. Os nomes são os que
       ele deu em 09/09. */
    <div className="saidas-do-relatorio">
      {ehMaster && (
        <button
          type="button"
          className="botao-secundario s-botao"
          onClick={() => setEmailAberto(true)}
          title="O assunto, o texto e o e-mail de quem recebe, no padrão da casa, prontos para copiar."
        >
          <Icone nome="email" tamanho={15} />
          E-mail
        </button>
      )}
      {emailAberto && <EmailDeEntrega relatorioId={relatorioId} aoFechar={() => setEmailAberto(false)} />}
      <button
        type="button"
        className="botao-secundario s-botao"
        disabled={gerando !== null}
        onClick={() => void baixar('pptx')}
        title="A versão didática, em PowerPoint — para a reunião por vídeo."
      >
        <Icone nome="tela" tamanho={15} />
        {gerando === 'pptx' ? 'Gerando…' : 'Apresentação'}
      </button>
      <button
        type="button"
        className="botao-primario s-botao"
        disabled={gerando !== null}
        onClick={() => void baixar('pdf')}
        title="O documento formal, em PDF — para ler, imprimir e anexar."
      >
        <Icone nome="relatorios" tamanho={15} />
        {gerando === 'pdf' ? 'Gerando…' : 'Relatório'}
      </button>
    </div>
  );
}

/**
 * "N de M tópicos escritos", em segmentos — sistema visual 2026, 14/09/2026.
 *
 * ⚠️ **NÃO é a contagem que saiu em 31/08/2026.** Aquela media `r.escritas`,
 * só o campo fixo, e diria "0 de 12" num relatório inteiro escrito nos campos
 * livres. Esta lê `respondida`, que vem do servidor com a MESMA regra que trava
 * a entrega (`lib/secao-respondida.ts`) — a mesma que acende a plaquinha de
 * cada tópico no índice. Duas contas para a mesma pergunta divergiriam.
 */
function AvancoDoRelatorio({ secoes }: { secoes: SecaoDoRelatorio[] }) {
  const escritos = secoes.filter((s) => s.respondida).length;
  return (
    <div className="rel-avanco">
      <span className="s-segmentos" aria-hidden="true">
        {secoes.map((s) => (
          <i key={s.id} className={s.respondida ? 'cheio' : undefined} />
        ))}
      </span>
      <span className="rel-avanco-texto">
        <strong>{escritos}</strong> de {secoes.length} tópicos escritos
      </span>
    </div>
  );
}

export function EditorDeRelatorio({
  id,
  aoAbrirCliente,
  aoExpirarSessao,
}: {
  id: string;
  aoAbrirCliente: (clienteId: string) => void;
  aoExpirarSessao: () => void;
}) {
  const [r, setR] = useState<Relatorio | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /**
   * ⭐ Quais campos de escrita os passos "O texto que voltou" estão mostrando.
   *
   * ⚠️ **Serve para a lista de campos não repeti-los:** o mesmo editor em dois
   * lugares da página brigaria pelo cursor.
   *
   * ⚠️ **É um CONJUNTO, não um id.** Um tópico pode ter mais de uma máquina de
   * prompt aberta ao mesmo tempo — a do capítulo 1 e a do resumo de documento
   * convivem na mesma seção. Com um id só, abrir a segunda devolveria a
   * primeira para a lista de baixo, e o mesmo texto apareceria duas vezes.
   */
  const [camposDoPasso, setCamposDoPasso] = useState<ReadonlySet<string>>(new Set());
  /**
   * ⭐ **O que falta apurar em cada tópico** — 24/09/2026.
   *
   * ⚠️ **Carrega uma vez por relatório**, e não por tópico: a trilha é do caso
   * inteiro, e pedi-la a cada clique seria a mesma resposta N vezes.
   */
  const [apuracao, setApuracao] = useState<ApuracaoDoTopico[]>([]);
  /** ⭐ A frase de escopo de cada tópico — pedido da Karen, 24/09/2026. */
  const [escopos, setEscopos] = useState<Array<{ chave: string; frase: string | null }>>([]);
  const marcarCampoDoPasso = useCallback((anotacaoId: string | null, aberto: boolean) => {
    if (!anotacaoId) return;
    setCamposDoPasso((antes) => {
      if (antes.has(anotacaoId) === aberto) return antes;
      const novo = new Set(antes);
      if (aberto) novo.add(anotacaoId);
      else novo.delete(anotacaoId);
      return novo;
    });
  }, []);

  /*
   * ⭐ **Qual tópico está aberto — 08/09/2026.**
   *
   * ⛔ **NADA abre sozinho ao entrar, e é correção dele:** *"quando entro na
   * página do relatório, já abre em OBJETO DA INVESTIGAÇÃO, mesmo sem eu
   * clicar em nada"*.
   *
   * A primeira versão escolhia o primeiro tópico para a página não nascer
   * vazia. **Mas abrir sozinho decide por quem chegou** — e num relatório de
   * catorze tópicos a chance de ser o certo é pequena. Agora a página abre com
   * a barra e um convite para escolher.
   *
   * ⚠️ **E não há memória entre visitas**, pelo mesmo motivo: voltar dias
   * depois direto num tópico que ele nem lembra ter aberto é a mesma surpresa,
   * só que adiada.
   */
  const [topicoAtual, setTopicoAtual] = useState<string>('');

  /* ⚠️ Muda a cada escolha: é o sinal que manda a `Secao` abrir. */
  const [aberturas, setAberturas] = useState(0);
  /*
   * ⭐⭐ **O QUE A CONFERÊNCIA ACHOU NO TEXTO ESCRITO À MÃO — 11/09/2026.**
   *
   * ⛔ **O conferidor não alcançava este caminho.** Ele rodava só dentro da
   * máquina de prompt; texto digitado ou colado direto no campo ia ao documento
   * sem passar por nada — e foi por aí que *"art. 50 do CC"* e *"sucessão
   * empresarial fraudulenta"* entraram no relatório do primeiro caso real, num
   * N2 que não contratou hipóteses jurídicas.
   *
   * ⚠️ **Estado de tela, e some ao recarregar — de propósito.** Conferência que
   * sobrevive à edição é conferência que mente: o texto mudou, o veredito velho
   * não vale mais. É a mesma decisão de `SumarioDoRelatorio`.
   */
  const [achadosDaSecao, setAchadosDaSecao] = useState<
    Record<string, Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>>
  >({});

  function escolherTopico(chave: string) {
    setTopicoAtual(chave);
    setAberturas((n) => n + 1);
  }

  async function carregar() {
    try {
      const lido = await apiRelatorios.ler(id);
      setR(lido);
      /* ⭐ O que falta apurar, junto com o relatório: é leitura, e falhar aqui
         não pode derrubar a tela do documento. */
      void apiRelatorios
        .apuracao(id)
        .then((x) => {
          setApuracao(x.topicos);
          setEscopos(x.escopos);
        })
        .catch(() => {
          setApuracao([]);
          setEscopos([]);
        });
      /*
       * ⚠️ **Tópico aberto que sumiu da estrutura volta a NENHUM.** As seções
       * nascem e saem numa sincronia; uma chave órfã deixaria a área abaixo da
       * barra **vazia**, sem nada escrito explicando por quê.
       */
      const chaves = (lido.secoes ?? []).map((s) => s.chave);
      if (topicoAtual && !chaves.includes(topicoAtual)) setTopicoAtual('');
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      setErro(e instanceof Error ? e.message : 'Não consegui abrir o relatório.');
    }
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!r) return;
    void apiCasos.ler(r.caso.id).then(setMarcosDoCaso).catch(() => setMarcosDoCaso(null));
  }, [r?.caso.id]);

  const ehMaster = usarEhMaster();

  /*
   * ⚠️ **A chave que refaz os desenhos.** O mapa, o painel e o quadro leem o
   * CASO, não o relatório — então nada no `r` muda quando alguém acrescenta um
   * investigado ou um bem. Este contador sobe a cada mudança na ficha e obriga
   * os três a buscar de novo: desenho velho na tela é pior que desenho nenhum,
   * porque quem olha acredita nele.
   */
  const [revisao, setRevisao] = useState(0);

  /*
   * ⚠️ **O caso completo, buscado à parte.** O relatório traz só um resumo do
   * caso (código, nível, prazo); os cinco marcos da dívida são do caso inteiro,
   * e é ele que o card edita. Copiá-los para dentro do relatório criaria duas
   * verdades sobre a mesma dívida.
   */
  const [marcosDoCaso, setMarcosDoCaso] = useState<Caso | null>(null);
  const chaveDosDesenhos = `${id}-${revisao}`;

  /* ⭐ O caminho mora na barra superior desde 14/09/2026 (`lib/trilha.ts`):
     o "← Relatórios" que abria a página virou o primeiro passo da trilha. */
  usarDefinirTrilha(r ? [{ rotulo: 'Relatórios', caminho: '/relatorios' }, { rotulo: r.codigo }] : null);

  if (!r) {
    return (
      <section className="pagina">
        {erro ? <p className="erro">{erro}</p> : <p className="vazio">Carregando…</p>}
      </section>
    );
  }

  const travado = r.situacao === 'entregue';

  async function mudarSituacao(situacao: Relatorio['situacao']) {
    try {
      setR(await apiRelatorios.situacao(id, situacao));
      avisar(
        situacao === 'entregue'
          ? 'Relatório entregue. A partir de agora ele não se edita.'
          : 'Situação atualizada.',
      );
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui mudar a situação.');
    }
  }

  return (
    /*
      ⭐⭐ **SISTEMA VISUAL 2026, parte 3 — 14/09/2026.** Régua aprovada por ele:
      `docs/sistema-visual-2026/proposta-relatorio-do-caso.png`. A identidade do
      relatório no alto; a dívida ao lado da cobertura; o índice de tópicos à
      esquerda e o tópico aberto à direita.

      ⛔ **Mudou lugar e aparência, não o que se grava.** Os mesmos componentes,
      com os mesmos dados e os mesmos botões, dentro de folhas novas. A antiga
      `pele-nova` (decisão 20) saiu daqui: o que ela provava virou este desenho.
    */
    <section className="pagina relatorio-2026">
      <section className="s-folha s-identidade rel-identidade">
        <div className="s-monograma rel-monograma" aria-hidden="true">
          <Icone nome="relatorios" tamanho={30} />
        </div>

        <div className="rel-identidade-texto">
          <span className="s-sobrelinha">Relatório do caso</span>
          <h1 className="titulo">{r.codigo}</h1>
          <p className="rel-meta">
            <button
              type="button"
              className="link-interno"
              onClick={() => aoAbrirCliente(r.cliente.id)}
            >
              {r.cliente.nome}
            </button>
            <span>
              caso <span className="dado">{r.caso.codigo}</span>
              {r.caso.titulo ? ` · ${r.caso.titulo}` : ''}
            </span>
          </p>
          <p className="rel-selos">
            {/*
              ⭐ **A tag colorida, e não texto solto — 08/09/2026.**

              > *"Deixar do mesmo jeito da tagzinha colorida que tem na página
              > de relatórios. E manter lá no topo onde já estava."* — Richard

              ⚠️ **É o MESMO `.selo-nivel` da lista**, não uma cópia: o nível de
              um caso tem que ser reconhecido igual em qualquer tela, e um
              segundo desenho divergiria na primeira correção.
            */}
            <span className={`selo-nivel selo-${r.caso.nivel}`} title={r.caso.nivelPergunta}>
              {r.caso.nivelNome}
            </span>
            {/* ⭐ A porcentagem escrita, ou a data da entrega, no lugar de
                "Rascunho" — 14/09/2026 (`lib/avanco-do-relatorio.ts`). */}
            <span className={avancoDoRelatorio(r).classe}>{avancoDoRelatorio(r).texto}</span>
            <PilulaDoFundo consumido={r.caso.fundo?.consumido} />
          </p>
        </div>

        <div className="rel-identidade-lado">
          {/*
            ⭐⭐ **OS BOTÕES DE SAÍDA VOLTARAM — 09/09/2026, pedido dele.**

            > *"Podemos acrescentar os botões geradores, chamados 'RELATÓRIO' e
            > 'APRESENTAÇÃO', no topo da página do relatório dos casos."* — Richard

            ⛔ **E não havia botão nenhum.** As três rotas estavam de pé no
            servidor desde sempre; a tela perdeu os botões em **20/08/2026**, no
            esvaziamento seção por seção — o comentário logo abaixo já dizia
            *"saíram os botões de saída"* e ninguém os repôs. É a terceira vez que
            o projeto encontra a mesma armadilha: **construído, ligado, sem tela**.

            ⚠️ **DOIS, e não três — decisão dele de 07/09/2026:** o relatório é
            PDF, a apresentação é PowerPoint. A saída em Word continua de pé no
            servidor e sem botão: saída que funciona não se apaga sem ele mandar.

            ⚠️ **No cabeçalho, ao lado do nome do caso.** É onde ele pediu, e é
            onde faz sentido: gerar o documento é ato sobre o relatório inteiro,
            não sobre a seção aberta.
          */}
          <SaidasDoRelatorio relatorioId={id} codigo={r.codigo} />
          {(r.secoes ?? []).length > 0 && <AvancoDoRelatorio secoes={r.secoes ?? []} />}
        </div>
      </section>

      {/*
        ⭐ **O aviso do relatório travado — 14/09/2026.** Desde que a entrega
        acompanha o caso, destravar também passa por lá: sem esta linha, quem
        abre um relatório entregue vê campos que não gravam e não sabe por quê.
      */}
      {/*
        ⭐⭐ **Apuração, histórico e — a pergunta dele — COMO FINALIZAR.**
        20/09/2026 (PRD §50). *"Quando finalizo o relatório? Pois sempre quando
        baixo o relatório para conferir algo, aparece rascunho."* A entrega é
        pelo CASO desde 14/09, e **nenhuma tela dizia isso**: o aviso abaixo
        explicava como DESTRAVAR, e nada explicava como ENTREGAR.
      */}
      <ApuracaoDoRelatorio r={r} ehMaster={ehMaster} aoMudar={setR} aoAbrirCliente={() => aoAbrirCliente(r.cliente.id)} />

      {travado && (
        <p className="aviso-atencao rel-travado">
          Entregue ao cliente{r.entregueEm ? ` em ${r.entregueEm.split('-').reverse().join('/')}` : ''}.
          O relatório está travado. Para mudar alguma coisa, volte o caso para a etapa{' '}
          <strong>Relatório</strong> na ficha do cliente.
        </p>
      )}

      {/*
        ⚠️ **Consulta sem serviço marcado é RESSALVA QUE NÃO SAI** — ou seja,
        uma limitação do trabalho que o cliente não vai conhecer.

        ⛔ **Isto nunca vai ao documento.** É defeito de cadastro do Hub, não
        limitação do levantamento: confessá-lo ao cliente seria pôr uma falha
        interna dentro de um documento assinado.

        A causa esperada é histórica: a marca de serviço nasceu em 29/08/2026, e
        tudo que foi consultado antes está sem ela. **Mas o aviso não some
        sozinho com o tempo** — se aparecer depois de uma busca nova, é porque
        alguém acrescentou uma consulta sem escrever a ressalva dela.
      */}
      {(r.consultasSemRessalva ?? 0) > 0 && (
        <p className="ajuda ressalvas-leitura">
          ⚠️ {r.consultasSemRessalva} consulta(s) deste caso não têm ressalva escrita, e por
          isso <strong>não aparecem nas limitações do documento</strong>. Consultas anteriores
          a 29/08/2026 não têm a marca de serviço. Se isto aparecer depois de uma busca nova,
          falta escrever a ressalva daquele levantamento.
        </p>
      )}

      {erro && <p className="erro">{erro}</p>}

      {/*
        ⚠️ **A TELA FOI ESVAZIADA em 20/08/2026 e recomeça daqui, seção por
        seção.** Saíram os botões de saída, o "Marcar entregue", a síntese, a
        ficha de investigados, os sinais, o mapa, as árvores, o quadro, o painel
        e o botão de contrato social. **Nada foi apagado do código** — o que se
        apagou foi a tela. Catálogo em `docs/CATALOGO-DE-AUTOMACOES.md`.
      */}

      {/*
        ⚠️ **O card da dívida vem ANTES de qualquer seção — 20/08/2026.**

        > *"Antes da seção INVESTIGADOS haverá um card com os campos que
        > precisaremos preencher manualmente."* — Richard

        Não é uma seção da estrutura: é o dado do CASO que dá sentido a todas
        elas. **A data da dívida é o eixo da investigação** — é contra ela que
        se comparam a transmissão do imóvel, a abertura da empresa e a entrada
        do sócio novo. Posto depois das seções, seria preenchido por último; no
        topo, é a primeira coisa que se resolve.
      */}
      {/*
        ⭐ **A dívida e a cobertura LADO A LADO — 14/09/2026, parte 3.** O
        comentário abaixo já dizia que o índice fica "a dois centímetros" do
        crédito que lhe dá sentido; agora fica literalmente ao lado.
      */}
      <div className="rel-duas">
      {marcosDoCaso && (
        <MarcosDaDivida
          caso={marcosDoCaso}
          aoMudar={async () => {
            setMarcosDoCaso(await apiCasos.ler(r!.caso.id));
          }}
        />
      )}

      {/*
        ⚠️ **O índice de cobertura MUDOU DE LUGAR em 25/08/2026, e o motivo é
        de uso, não de estética.** Ele nasceu dentro de ATIVOS LOCALIZADOS, na
        seção mãe — e o Richard não o achou. **A mãe é a única seção sem lista,
        e por isso a que ninguém abre.**

        ⭐ **Aqui ele fica ao lado do que lhe dá sentido:** o card acima é onde o
        crédito do cliente é preenchido, e o índice é justamente esse crédito
        confrontado com o patrimônio localizado. Sem o crédito, o índice diz o
        que falta — e o campo que falta está a dois centímetros dele.

        ⚠️ **E é do CASO, como o card**: o crédito é um só, o patrimônio é de
        todas as partes investigadas juntas.
      */}
      <IndiceDeCobertura casoId={r.caso.id} chave={`cobertura-${r.escritas}`} desenho="folha" />
      </div>

      {r.total === 0 ? (
        <div className="bloco">
          <p className="vazio">
            <strong>Este relatório está em branco.</strong> A estrutura do
            documento está sendo definida do zero — seção por seção, com o
            propósito, a automação e os níveis de cada uma.
          </p>
        </div>
      ) : null}
      {/*
        ⛔ **A CONTAGEM DE SEÇÕES ESCRITAS SAIU — 31/08/2026, decisão dele.**

        ⭐ **E ela tinha deixado de dizer a verdade no mesmo dia.** Com o campo
        fixo escondido, o trabalho passou a viver nos campos livres — e a
        contagem media só o campo fixo. Ela diria "0 de 12 escritas" num
        relatório inteiro respondido.

        ⚠️ **`r.escritas` CONTINUA vindo do servidor, e não é enfeite:** é o
        gatilho que faz dez painéis se refazerem quando a seção muda. Tirá-lo do
        contrato pararia o índice de cobertura, o endividamento, o quadro, os
        cinco painéis de ativos e o anexo notarial de se atualizar — sem erro
        nenhum, que é como esta casa perde funcionalidade.
      */}

      {/*
        ⚠️ **Cada seção abre e fecha, e o estado é lembrado — 20/08/2026.**

        > *"Cada seção que formos criando, terão aquelas setinhas para expandir e
        > retrair a seção/campo."* — Richard

        ⚠️ **Reusa o `Secao` que a ficha do cliente já usa**, e não uma sanfona
        nova: o comportamento da setinha, o alvo de clique no título inteiro e a
        memória por seção no navegador já estavam resolvidos ali. Duas sanfonas
        divergiriam na primeira correção feita só numa delas.

        ⚠️ **O `id` leva o prefixo `rel-`** porque a chave da seção convive com
        as da ficha do cliente no mesmo `localStorage` — `investigados` existe
        nos dois lugares e são coisas diferentes.
      */}
      {/*
        ⚠️ **A subseção é desenhada RECUADA — 24/08/2026.** Sem o recuo, "Imóveis"
        apareceria no mesmo peso de "Ativos localizados", e a hierarquia que ele
        pediu existiria só no banco. **Estrutura que não se vê na tela é
        estrutura que ninguém respeita ao escrever.**

        ⭐ **Desde 14/09/2026 o recuo mora no ÍNDICE**, e o tópico aberto diz o
        nome da mãe acima do título. A regra continua: a hierarquia se vê.
      */}
      {/*
        ⭐ **A BARRA DE TÓPICOS — 08/09/2026, e ela substituiu a pilha.**

        > *"Estou achando muito confuso navegar pelas seções e subseções do
        > relatório dos casos."* — Richard, mandando o CENPROT como referência

        ⛔ **As 14 seções ficavam empilhadas na mesma página**, cada uma com sua
        setinha e as subseções apenas recuadas: para chegar à última era preciso
        rolar por todas, e nada dizia onde se estava.

        ⚠️ **Agora um tópico por vez, e foi decisão dele** — a alternativa, a
        barra só rolando até a seção, manteria a página longa, que é a causa.

        ⭐ **14/09/2026: a barra virou ÍNDICE em coluna, ao lado do tópico** —
        sistema visual 2026, parte 3. Um tópico por vez continua valendo; o que
        muda é que todos os tópicos ficam à vista sem abrir lista nenhuma.
      */}
      {(r.secoes ?? []).length > 0 && (
      <div className="rel-bancada">
      <MenuDoRelatorio
        topicos={(r.secoes ?? []).map((s) => ({
          chave: s.chave,
          titulo: s.titulo,
          /* ⚠️ Seção sem capítulo não some da barra: ganha um grupo próprio.
             Sumir faria um tópico existir no documento e não na navegação. */
          grupo: s.parte || 'Outros',
          respondida: s.respondida,
          paiChave: s.paiChave,
        }))}
        atual={topicoAtual}
        aoEscolher={escolherTopico}
      />

      <div className="rel-mesa">

      {/*
        ⚠️ **A área nasce com um CONVITE, não vazia.** Sem nada escrito abaixo da
        barra, a página parece ter falhado ao carregar — e quem chega não
        descobre sozinho que precisa escolher um tópico ali em cima.
      */}
      {!topicoAtual && (
        <p className="s-folha escolha-um-topico">
          Escolha um tópico no índice ao lado para escrever.{' '}
          {(r.secoes ?? []).filter((s) => !s.respondida).length > 0 && (
            <>
              A plaquinha marca os que ainda estão em branco —{' '}
              <strong>
                {(r.secoes ?? []).filter((s) => !s.respondida).length} de {(r.secoes ?? []).length}
              </strong>
              .
            </>
          )}
        </p>
      )}

      {(r.secoes ?? [])
        .filter((s) => s.chave === topicoAtual)
        .map((s) => (
        <div key={s.id} className="rel-topico-aberto">
          <Secao
            desenho="folha"
            id={`rel-${s.chave}`}
            /*
              ⭐ **O capítulo e a situação acima do título — 14/09/2026.** Era a
              linha dourada solta acima da seção. ⚠️ **A subseção diz a mãe**
              ("… · Mapeamento patrimonial"): o recuo que mostrava a hierarquia
              mudou-se para o índice ao lado, e sem o nome da mãe aqui
              "Imóveis" pareceria capítulo próprio.
            */
            antes={
              <p className="rel-mesa-capitulo">
                <span className="s-micro">
                  {[
                    s.parte,
                    s.paiChave
                      ? (r.secoes ?? []).find((m) => m.chave === s.paiChave)?.titulo
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {s.respondida ? (
                  <span className="s-pilula">escrito</span>
                ) : (
                  <span className="s-pilula s-pilula-quieta rel-pilula-em-branco">
                    <Icone nome="atencao" tamanho={13} />
                    ainda em branco
                  </span>
                )}
              </p>
            }
            titulo={s.titulo}
            subtitulo={s.proposito}
            /*
              ⚠️ **Abre ao ser escolhida — 08/09/2026.** Com um tópico por vez,
              clicar na barra e receber uma sanfona fechada seriam dois cliques
              para uma decisão só.

              ⭐ **Reusa o `abrirEm` que a `Secao` já tem** — o contador que a
              ficha do cliente usa quando um filtro muda a lista. Uma prop nova
              faria duas maneiras de dizer "abra", e elas divergiriam.
            */
            abrirEm={aberturas}
            acao={
              s.natureza !== 'fato' ? (
                <span className={`selo-natureza selo-${s.natureza}`}>
                  {s.natureza === 'achado' ? 'análise' : 'recomendação'}
                </span>
              ) : undefined
            }
          >
            {/*
              ⚠️ **O que cada seção mostra ALÉM do campo livre, indexado pela
              chave — 20/08/2026.**

              > *"Além do campo para escrever livre, haverá uma tabela… **porque
              > será dessa tabela que iremos vincular as automações que houver
              > nesse relatório**."* — Richard

              A tabela vem ANTES do campo de propósito: ela é o dado, o campo é
              a análise sobre o dado. Quem escreve precisa da lista à vista,
              acima da caixa de texto — e não abaixo, onde só se vê depois de
              rolar.

              ⚠️ **Este é o ponto de extensão da estrutura nova.** Cada seção que
              o Richard definir com uma peça própria entra aqui, pela chave. É a
              mesma amarração que foi desligada em 20/08 — a diferença é que
              agora ela existe porque ele apontou, e não por herança.
            */}
            {/*
              ⭐⭐ **O QUE FALTA APURAR NESTE TÓPICO — 24/09/2026, Karen.**

              ⛔⛔ **O texto de resultado negativo existia e estava calado.** No
              caso 2026-003 nenhum dos seis tópicos de ativo estava autorizado
              a afirmar ausência, e a tela não dizia por quê: o tópico ficava
              mudo, e mudo não distingue *"não achamos"* de *"não procuramos"*.
              Foi por esse silêncio que o tópico Imóveis saiu afirmando à mão
              que nada fora localizado, com a busca por titular **não feita em
              nenhuma das quatro partes**.

              ⚠️ **Vem ANTES da peça do tópico**, que é onde a decisão de
              escrever ou não é tomada.
            */}
            <ApuracaoDoTopicoNaTela chave={s.chave} apuracao={apuracao} escopos={escopos} />

            {/* ⭐ No desenho de Imóveis desde 19/09/2026 (PRD §46.6) — as mesmas peças, em outro lugar. */}
            {s.chave === 'investigados' && (
              <QualificacaoDoCaso
                casoId={r.caso.id}
                aoIrAoTopico={escolherTopico}
                chave={`${s.id}-${r.escritas}`}
                /* ⭐ Os campos livres por pessoa — 31/08/2026. A lista inteira
                   desce, e cada investigado filtra a sua. */
                anotacoes={{
                  relatorioId: id,
                  secaoId: s.id,
                  lista: s.anotacoes,
                  travado,
                  aoMudar: carregar,
                }}
              />
            )}

            {/*
              ⭐ **O CAPÍTULO 1 — a abertura, 07/09/2026.**

              ⚠️ **O quadro das partes fica só na seção do ESCOPO.** Repeti-lo na
              do objeto poria a mesma tabela duas vezes na mesma página — e é o
              mesmo erro que a seção mãe de ATIVOS quase cometeu em 25/08.

              ⚠️ **A "Finalidade" não recebe proposta nenhuma, e é decisão.** É o
              que ESTE cliente quer decidir: não se deduz do banco, e um texto
              genérico ali valeria menos que o silêncio.

              ⚠️ **A carta de apresentação NÃO aparece aqui.** Ela abre o
              capítulo no documento, é igual em todos e ninguém a edita — pôr um
              campo dela na tela convidaria a editar o que não se edita.
            */}
            {/*
              ⭐ **ESCOPO e PARTES separados — 09/09/2026, a pedido dele.**

              > *"Acredito que seria melhor separar ESCOPO e PARTES
              > INVESTIGADAS."* — Richard

              ⛔ **Uma seção mostrava o texto do ESCOPO com o quadro das PARTES
              embaixo**, sob um título que prometia as duas. São fronteiras
              diferentes: o escopo diz o que ficou fora da PESQUISA, as partes
              dizem quem ficou fora da LISTA.

              ⚠️ **A proposta de texto acompanha o escopo**, que é de onde ela
              sempre veio; o quadro fica com as partes, que é o que ele desenha.
            */}
            {s.chave === 'apresentacao-objeto' && (
              <AberturaDoRelatorio
                relatorioId={id}
                secaoId={s.id}
                peca="objeto"
                comQuadro={false}
                travado={travado}
                jaEscrito={s.conteudo.trim().length > 0}
                aoGravar={carregar}
                campo={{
                  relatorioId: id,
                  secaoId: s.id,
                  anotacoes: s.anotacoes,
                  aoMudar: carregar,
                  aoAbrirCampo: marcarCampoDoPasso,
                }}
                chave={`${s.id}-${r.escritas}`}
              />
            )}

            {/* ⭐ As PARTES ficam com o quadro, que é o que elas desenham. */}
            {s.chave === 'apresentacao-partes' && (
              <AberturaDoRelatorio
                relatorioId={id}
                secaoId={s.id}
                peca="partes"
                comQuadro
                travado={travado}
                jaEscrito={s.conteudo.trim().length > 0}
                aoGravar={carregar}
                campo={{
                  relatorioId: id,
                  secaoId: s.id,
                  anotacoes: s.anotacoes,
                  aoMudar: carregar,
                  aoAbrirCampo: marcarCampoDoPasso,
                }}
                chave={`${s.id}-${r.escritas}`}
              />
            )}

            {/*
              ⭐⭐ **A MÁQUINA DO SUMÁRIO EXECUTIVO — 08/09/2026.**

              > *"O SUMÁRIO EXECUTIVO gere o prompt completo e perfeito […] eu
              > copio esse prompt e colo no Claude ou GPT via chat, e o que
              > gerar lá eu copio e colo no SUMÁRIO EXECUTIVO."* — Richard

              ⚠️ **O Hub não fala com IA nenhuma.** Não há chave, chamada nem
              integração: ele monta um texto e devolve. Quem o leva ao chat é
              uma pessoa, caso a caso — e é isso que preserva o essencial da
              regra do `CLAUDE.md` enquanto ele tem a redação que pediu.

              ⚠️ **Só na seção MÃE**, como o `docs/PRD.md` §37 decidiu: as duas
              subseções ancoram desenhos, e texto por cima de desenho repete a
              figura.
            */}
            {s.chave === 'sumario' && (
              <SumarioDoRelatorio
                relatorioId={id}
                travado={travado}
                jaEscrito={s.conteudo.trim() !== ''}
                campo={{
                  relatorioId: id,
                  secaoId: s.id,
                  anotacoes: s.anotacoes,
                  aoMudar: carregar,
                  aoAbrirCampo: marcarCampoDoPasso,
                }}
                aoAceitar={async (texto) => {
                  await apiRelatorios.acrescentar(id, s.id, texto);
                  await carregar();
                }}
              />
            )}

            {/*
              ⭐⭐ **O CAPÍTULO 5 — hipóteses e recomendações, 09/09/2026.**

              > *"Precisamos fazer uma seção onde iremos colocar o 'parecer'
              > final, aqui precisa analisar TUDO que está no caso, para
              > verificar possibilidades, de possíveis fraudes (ou indícios) e
              > as soluções encontradas."* — Richard, 08/09/2026

              ⚠️ **A MESMA máquina do sumário, com outro prompt.** Os três
              passos são idênticos — gerar, levar ao chat, conferir e gravar —
              e três telas iguais divergiriam na primeira correção feita só
              numa delas. O que muda mora no servidor, que é onde as travas
              precisam estar.

              ⛔ **Em caso que não é N3, a rota devolve o aviso no lugar do
              prompt** — e nem a seção aparece na tela, porque a estrutura já a
              deixa fora pelo `aPartirDe`. São duas trancas para a mesma porta,
              de propósito: gerar aqui seria oferecer trabalho que não foi
              vendido.
            */}
            {/*
              ⭐ **A terceira peça do capítulo 5 — 10/09/2026.**

              > *"Nesse campo precisa gerar um prompt completo, com a temática
              > de conclusão sem dar palpites e sim, perante as informações do
              > caso, concluir o caso."* — Richard

              ⚠️ **A mesma máquina, e agora em relatório de QUALQUER nível.**
              As duas de cima são entrega do Kairós e a rota recusa fora dele;
              esta vale nos três, e é por isso que a lista tem três chaves em
              vez de duas.
            */}
            {(s.chave === 'hipoteses-juridicas' ||
              s.chave === 'recomendacoes' ||
              s.chave === 'consideracoes-finais') && (
              <SumarioDoRelatorio
                relatorioId={id}
                peca={
                  s.chave === 'hipoteses-juridicas'
                    ? 'hipoteses'
                    : s.chave === 'recomendacoes'
                      ? 'recomendacoes'
                      : 'consideracoes'
                }
                travado={travado}
                jaEscrito={s.conteudo.trim() !== ''}
                campo={{
                  relatorioId: id,
                  secaoId: s.id,
                  anotacoes: s.anotacoes,
                  aoMudar: carregar,
                  aoAbrirCampo: marcarCampoDoPasso,
                }}
                aoAceitar={async (texto) => {
                  await apiRelatorios.acrescentar(id, s.id, texto);
                  await carregar();
                }}
              />
            )}

            {/* ⭐ No desenho de Imóveis desde 19/09/2026 (PRD §46.5) — as mesmas peças, em outro lugar. */}
            {s.chave === 'endividamento' && (
              <PassivoDoCaso casoId={r.caso.id} chave={`${s.id}-${r.escritas}`} />
            )}

            {/*
              ⚠️ **A seção começa pelo ORGANOGRAMA, e só por ele** — decisão do
              Richard, 21/08/2026, depois de ver e recusar uma primeira versão
              com quadro societário e contrato social juntos.

              > *"Iniciaremos com o organograma do investigado com onde ele tem
              > vínculo societário [...] na seção INVESTIGADOS, em QUALIFICAÇÃO
              > » Participações societárias, já terá as participações — podemos
              > linkar/utilizar dessas informações que já temos."*

              ⚠️ **Nada é consultado aqui.** O desenho lê a ficha do caso, que a
              busca da seção INVESTIGADOS já preencheu. Repetir a consulta numa
              segunda tela gastaria de novo por um dado que já está no Hub.
            */}
            {s.chave === 'societaria' && (
              <>
                <MapaDeVinculos casoId={r.caso.id} chave={chaveDosDesenhos} />
                {/* ⭐ De quando é o quadro que o desenho acima afirma. */}
                <IdadeDaBase />
                {/* ⭐ Sócio com domicílio no exterior — decisão 16, 13/09/2026. */}
                <SociosNoExterior casoId={r.caso.id} chave={chaveDosDesenhos} />
                {/*
                  ⛔ **O BOTÃO VOLTOU EM 08/09/2026 — ele existia e ninguém
                  alcançava.**

                  > *"Adicione um botão 'analisar contrato social', onde irei
                  > realizar o upload de todos os contratos sociais e você vai
                  > extrair as informações cronologicamente."* — Richard,
                  > 20/08/2026

                  ⛔ **Construído, LIGADO no servidor desde 28/08, registrado no
                  `RETOMAR.md` como ✅ — e fora de toda tela desde 20/08**, quando
                  o editor foi esvaziado seção por seção. **Zero documentos no
                  banco:** ele nunca teve como clicar. É a lição de 24/08 outra
                  vez — *funcionalidade marcada como pronta pode nunca ter
                  rodado*, e o oráculo é o banco.

                  ⚠️ **ABAIXO do organograma, e não ao lado.** Em 21/08 ele viu
                  uma versão com *"quadro societário e contrato social juntos"* e
                  a recusou, pedindo começar pelo organograma. A seção continua
                  começando por ele; o que muda é que o caminho seguinte deixa de
                  ser invisível.
                */}
                <ContratosSociais
                  casoId={r.caso.id}
                  /* ⭐ 09/09/2026 — o resumo de um documento vira campo livre
                     desta seção, e por isso ela precisa saber onde escrever. */
                  relatorioId={id}
                  secaoId={s.id}
                  travado={travado}
                  campo={{
                    relatorioId: id,
                    secaoId: s.id,
                    anotacoes: s.anotacoes,
                    aoMudar: carregar,
                    aoAbrirCampo: marcarCampoDoPasso,
                  }}
                  aoGravarSecao={carregar}
                />
              </>
            )}

            {/*
              ⚠️ **As cinco subseções de ATIVOS LOCALIZADOS usam UM componente
              só, filtrado pela categoria** — decisão do Richard, 24/08/2026.
              Cinco telas iguais divergiriam na primeira correção feita só numa
              delas.

              ⚠️ **A seção MÃE não mostra lista nenhuma**, e é de propósito: o
              lançamento vive na subseção da categoria. Repetir tudo na mãe
              faria o mesmo bem aparecer duas vezes na mesma página.
            */}
            {/*
              ⚠️ **O PAINEL e o QUADRO voltaram em 25/08/2026 — e estavam
              ÓRFÃOS, não desligados.** Os dois componentes existiam inteiros
              neste arquivo desde 20/08 e **nenhuma linha os chamava**.
              Componente órfão não some com erro: some em silêncio — a mesma
              armadilha que já tinha engolido o `EstruturaDoDocumento`.

              ⭐ **A fonte deles mudou junto.** Somavam o campo de valor da ficha
              do investigado; agora leem `AtivoDoInvestigado`, que é onde o
              patrimônio vive desde 24/08. Religar sem trocar faria o painel
              mostrar um patrimônio e esta seção mostrar outro — e, pior, o
              painel não conhecia a trava do que não soma.

              ⚠️ **Ficam na seção MÃE, e isto substitui a decisão tomada horas
              antes no mesmo dia.** Pela manhã ele escolheu o topo, ao lado do
              índice de cobertura, com o risco à vista: era na mãe que o índice
              se perdera no dia anterior. **Viu montado e decidiu o contrário** —
              *"esses blocos/tabelas deverão estar em Ativos localizados"*. As
              duas decisões foram dele, e a troca fica escrita.

              ⭐ **E a razão do risco antigo caiu junto.** A mãe se perdia por ser
              *"a única seção sem lista, e por isso a que ninguém abre"*. Com o
              painel e o quadro dentro, ela deixa de ser vazia.

              ⚠️ **Na MÃE, nunca numa subseção.** Os dois resumem as cinco
              categorias juntas: pendurados em `ativos-imoveis`, diriam respeito
              só aos imóveis e contariam veículo no lugar errado.
            */}
            {/*
              ⭐ **AS ÁRVORES, item 12 do catálogo — ligadas em 28/08/2026.**

              Construídas em 20/08 e desligadas no mesmo dia por não haver
              seção que as ancorasse. A estrutura que ele entregou em 28/08 lhes
              deu casa: *"RELAÇÕES FAMILIARES RELEVANTES – árvore genealógica
              das pessoas físicas investigadas"*.

              ⚠️ **A chave foi conferida no banco antes de ligar.** Ancorar em
              chave que não existe é o defeito que escondeu o organograma por
              quatro dias e estas árvores por oito.
            */}
            {/*
              ⭐ **O ANEXO ganhou SEÇÃO PRÓPRIA — 08/09/2026.**

              > *"Por que está aparecendo um 'Anexo · Atos notariais' lá no
              > final da página de qualquer seção? Ter uma seção só com os
              > anexos."* — Richard

              ⛔ **Ele vivia FORA do laço de seções.** Enquanto as catorze
              ficavam empilhadas, "no fim da página" era o fim do relatório —
              com um tópico por vez, virou o rodapé de qualquer tópico aberto,
              e a relação de atos aparecia embaixo de "Objeto da investigação".
            */}
            {/*
              ⭐⭐ **A LINHA DO TEMPO — 08/09/2026.**

              Entrega declarada do N2 no portfólio: *"linha do tempo de
              alienações"*. ⭐ **Nenhuma consulta acontece:** os fatos já estão
              no caso, e o que faltava era vê-los na mesma régua.

              ⛔ **Ela ordena e não conclui** — a regra do método, em letras
              maiúsculas: *"a transmissão, isoladamente, NÃO autoriza conclusão
              de fraude"*.
            */}
            {/* ⭐⭐ Sinais de blindagem, camuflagem ou fraude — 15/09/2026. O
                tópico existe para os indícios; a caracterização não se faz aqui. */}
            {s.chave === 'grupo-economico' && (
              <SinaisDeBlindagem casoId={r.caso.id} chave={chaveDosDesenhos} />
            )}

            {s.chave === 'linha-do-tempo' && (
              <LinhaDoTempo relatorioId={id} chave={`linha-${r.escritas}`} />
            )}
            {/*
              ⭐ 16/10/2026 (PRD §52): o texto da Linha do tempo sai pelo prompt,
              a mesma máquina do índice. O desenho continua como está; o prompt
              leva os mesmos fatos dele, e a resposta entra num campo novo do
              tópico, que confere ao gravar. Nada do que já está escrito muda.
            */}
            {s.chave === 'linha-do-tempo' && ehMaster && !travado && (
              <SumarioDoRelatorio
                travado={travado}
                redacao="linha-do-tempo"
                campo={{
                  relatorioId: id,
                  secaoId: s.id,
                  anotacoes: s.anotacoes,
                  aoMudar: carregar,
                  aoAbrirCampo: marcarCampoDoPasso,
                }}
                fonte={{
                  apoio:
                    'O Hub monta o prompt com os mesmos fatos da linha do tempo acima, na mesma ordem, ' +
                    'ao lado das datas da dívida, e pede o texto que descreve a sequência e a distância ' +
                    'entre os fatos. ⛔ O prompt proíbe concluir sobre a proximidade, e os sinais apurados ' +
                    'sobre vínculos não vão.',
                  rotuloGravar: s.conteudo.trim() ? 'Acrescentar ao fim desta seção' : 'Gravar na seção',
                  gerar: async () => (await apiRelatorios.promptDaLinhaDoTempo(id)).prompt,
                  conferir: async (texto) => (await apiRelatorios.conferirSumario(id, texto)).achados,
                }}
                aoAceitar={async (texto) => {
                  await apiRelatorios.acrescentar(id, s.id, texto);
                  await carregar();
                }}
              />
            )}

            {s.chave === 'anexos' && (
              <AnexoNotarial casoId={r.caso.id} chave={`anexo-${r.escritas}`} />
            )}

            {s.chave === 'sumario-familiares' && (
              <ArvoresGenealogicas casoId={r.caso.id} chave={chaveDosDesenhos} />
            )}

            {/*
              ⭐ **O PAINEL E O QUADRO MUDARAM PARA O SUMÁRIO — 08/09/2026.**

              > *"Essas tabelinhas de itens localizados deverão estar na seção
              > de SUMÁRIO EXECUTIVO."* — Richard

              ⚠️ **É o segundo endereço deles.** Em 28/08 foram para dentro de
              ATIVOS LOCALIZADOS, também a pedido dele; agora sobem para o
              sumário. **A razão nova é melhor que a antiga:** o sumário é o
              retrato do apurado, e um retrato começa pelo número. Quem lê só
              este capítulo — que é o desenho declarado dele — sai sabendo
              quanto e de quem.

              ⚠️ **E o mapeamento patrimonial não fica sem nada:** ele tem as
              subseções por espécie, que é onde o lançamento vive. O que sobe é
              o RESUMO, não a matéria.
            */}
            {s.chave === 'sumario' && (
              <>
                <PainelDeAtivos casoId={r.caso.id} chave={chaveDosDesenhos} />
                <QuadroPorQualificacao
                  casoId={r.caso.id}
                  nivel={r.caso.nivel}
                  chave={`quadro-${r.escritas}`}
                />
              </>
            )}

            {/* ⭐ 07/10/2026 — o índice de probabilidade de recuperação, escolhido pelo master. */}
            {s.chave === 'sumario-recuperacao' && (
              <IndiceDeRecuperacao
                relatorioId={r.id}
                nivel={r.probabilidadeDeRecuperacao}
                entregue={r.situacao === 'entregue'}
                ehMaster={ehMaster}
                aoMudar={(nivel) => setR((x) => (x ? { ...x, probabilidadeDeRecuperacao: nivel } : x))}
              />
            )}
            {/* ⭐ 08/10/2026 — o prompt da justificativa, a mesma máquina das outras seções.
                Só depois do nível escolhido: sem ele, não há o que justificar. */}
            {s.chave === 'sumario-recuperacao' && ehMaster && !travado && r.probabilidadeDeRecuperacao && (
              <SumarioDoRelatorio
                travado={travado}
                redacao="indice"
                campo={{
                  relatorioId: id,
                  secaoId: s.id,
                  anotacoes: s.anotacoes,
                  aoMudar: carregar,
                  aoAbrirCampo: marcarCampoDoPasso,
                }}
                fonte={{
                  apoio:
                    'O Hub monta o prompt com o nível escolhido acima e o material do caso, e pede a ' +
                    'justificativa: o que sustenta o nível, o que pesa contra e o que está em aberto. ' +
                    '⛔ O prompt não escolhe nem discute o nível.',
                  rotuloGravar: s.conteudo.trim() ? 'Acrescentar ao fim desta seção' : 'Gravar na seção',
                  gerar: async () => (await apiRelatorios.promptDoIndice(id)).prompt,
                  conferir: async (texto) => (await apiRelatorios.conferirSumario(id, texto)).achados,
                }}
                aoAceitar={async (texto) => {
                  await apiRelatorios.acrescentar(id, s.id, texto);
                  await carregar();
                }}
              />
            )}

            {/* ⭐ O tópico Imóveis repaginado — 16/09/2026 (lista + passo a passo). */}
            {s.chave === 'ativos-imoveis' && (
              <ImoveisDoCaso
                casoId={r.caso.id}
                nivel={r.caso.nivel}
                chave={`${s.id}-${r.escritas}`}
                aoMudarPesquisa={() => void carregar()}
              />
            )}
            {/* ⭐ Veículos no desenho de Imóveis — 18/09/2026, pedido dele: Imóveis é a referência visual. */}
            {s.chave === 'ativos-veiculos' && <VeiculosDoCaso casoId={r.caso.id} chave={`${s.id}-${r.escritas}`} />}
            {/* ⭐ Outros ativos — ações, marcas, patentes, direitos minerários (18/09/2026). */}
            {s.chave === 'ativos-outros' && (
              <OutrosDoCaso casoId={r.caso.id} chave={`${s.id}-${r.escritas}`} aoMudarPesquisa={() => void carregar()} />
            )}
            {/* ⭐ Um tópico só desde 08/09/2026: a pesquisa da central traz as
                duas espécies juntas, e a tela as distingue por selo e filtro. */}
            {s.chave === 'ativos-atos' && <AtosDoCaso casoId={r.caso.id} chave={`${s.id}-${r.escritas}`} />}
            {s.chave === 'ativos-processos' && <ProcessosDoCaso casoId={r.caso.id} chave={`${s.id}-${r.escritas}`} />}

            {/*
              ⭐⭐ **As pesquisas feitas fora do Hub — decisão 14, 13/09/2026.**
              O registro fica no capítulo onde a ressalva dela imprime: é ali
              que o capítulo vazio precisa dizer que se olhou.
            */}
            {(PESQUISAS_DO_CAPITULO[s.chave] ?? []).map((servico) => (
              <PesquisaFeitaFora
                key={servico}
                casoId={r.caso.id}
                servico={servico}
                aoMudar={() => void carregar()}
              />
            ))}

            {/*
              ⛔ **O CAMPO FIXO SÓ APARECE QUANDO TEM TEXTO — 31/08/2026.**

              > *"Retirar os campos de escrita livres de forma fixa de todas as
              > seções, pois quando eu quiser digitar algo, eu clico em
              > '+ acrescentar campo de escrita'."* — Richard

              ⚠️ **Some quando vazio, e NÃO deixa de existir.** A diferença é o
              que salva a única automação viva: a leitura do contrato social
              grava em `conteudo` da seção societária. Se o campo desaparecesse
              de vez, esse texto ficaria escrito e invisível — o defeito que já
              escondeu o organograma por quatro dias nesta casa.

              ⭐ **Com texto, ele aparece e é editável.** Sem texto, a seção
              abre limpa e quem quiser escrever clica no botão abaixo.
            */}
            {s.conteudo.trim().length > 0 && (
              <>
                <Campo
                  rotulo={s.titulo}
                  rotuloOculto
                  valor={s.conteudo}
                  travado={travado}
                  aoGravar={async (texto) => {
                    const r = await apiRelatorios.escrever(id, s.id, texto);
                    setAchadosDaSecao((a) => ({ ...a, [s.id]: r.achados }));
                    await carregar();
                  }}
                />
                {/*
                  ⛔ **Avisa e não bloqueia.** O texto já está gravado quando isto
                  aparece — quem decide o que vale no documento assinado é ele.
                */}
                {(achadosDaSecao[s.id] ?? []).length > 0 && (
                  <div className="aviso-atencao">
                    <p className="rotulo">A conferência achou isto no texto desta seção</p>
                    <ul className="atos-lidos">
                      {(achadosDaSecao[s.id] ?? []).map((a, i) => (
                        /* ⚠️ O índice na chave — 12/09/2026: dois achados
                           podem ter aviso e trecho iguais, e chave repetida
                           faz o React trocar um aviso pelo outro. */
                        <li key={`${i}-${a.aviso}`}>
                          <strong>{a.aviso}</strong>
                          {/* ⚠️ O recorte é o que faz o aviso ser procurável: sem
                              ele, "há base legal no texto" manda reler tudo. */}
                          <br />
                          <span className="ajuda">{a.trecho}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {/*
              ⭐ **OS CAMPOS LIVRES — 28/08/2026.**

              > *"Gostaria que esses campos eu pudesse adicionar conforme a
              > minha necessidade, uma espécie de 'anotação' dentro da
              > seção."* — Richard

              ⚠️ **Vêm DEPOIS do campo principal, e é a mesma ordem do
              documento.** Bloco em lugar diferente numa das saídas é
              divergência disfarçada de arrumação (`docs/PRD.md` §21).
            */}
            {/*
              ⚠️ **Aqui só os campos DA SEÇÃO — 31/08/2026.** Os que pertencem a
              um investigado aparecem sob ele, na lista acima; mostrá-los nos
              dois lugares faria o mesmo texto parecer dois.
            */}
            {/*
              ⭐ 16/10/2026: no Anexos, cada campo é um anexo. *"No tópico anexos,
              não tem o botão para anexar pdf ou imagem, bem como ter a
              possibilidade de colocar 'título' para aquele anexo ou a descrição
              do anexo."* O campo já tinha as três coisas (título, texto e o botão
              de anexar); faltava dizer isso aqui. ⚠️ Com o relatório entregue,
              nada se acrescenta: volta-se o caso para destravar.
            */}
            {s.chave === 'anexos' && !travado && (
              <p className="ajuda">
                Cada anexo é um campo: no título, o nome do anexo; no texto, a descrição; e o botão
                "Anexar PDF ou imagem", na faixa do campo, traz o arquivo.
              </p>
            )}
            <Anotacoes
              relatorioId={id}
              secaoId={s.id}
              /* ⚠️ O campo que o passo do prompt está mostrando não se repete
                 aqui: o mesmo editor em dois lugares brigaria pelo cursor. */
              anotacoes={s.anotacoes.filter((a) => !a.investigadoId && !camposDoPasso.has(a.id))}
              travado={travado}
              aoMudar={carregar}
              {...(s.chave === 'anexos' ? { rotulo: '+ Acrescentar anexo', tituloSugerido: 'Título do anexo' } : {})}
            />

            {/*
              ── As RESSALVAS fecham o capítulo, aqui e no papel ──────────────

              ⭐ Item 1.4 da estrutura de 28/08/2026, construído em 29/08:
              **coladas em cada capítulo**, por decisão do Richard.

              ⚠️ **Depois das anotações, exatamente como no `.docx`.** Bloco em
              lugar diferente numa das saídas é divergência disfarçada de
              arrumação (`docs/PRD.md` §21).

              ⚠️ **Este texto é o do CLIENTE, e não o da tela.** A ajuda de cada
              botão de busca diz o preço e a fornecedora; nada disso sai da
              KAIRÓS. Ver `api/src/lib/ressalvas-por-servico.ts`.
            */}
            {(r.ressalvas?.[s.chave] ?? []).map((rs) => (
              <div className="ressalva-do-capitulo" key={rs.levantamento}>
                <p className="rotulo">{rs.levantamento}</p>
                <p>{rs.texto}</p>
              </div>
            ))}
          </Secao>
        </div>
      ))}
      </div>
      </div>
      )}

    </section>
  );
}

/**
 * O anexo com a relação de atos notariais, uma tabela por investigado.
 *
 * ⚠️ **Não aparece quando não há pesquisa na central**, e é o que torna este
 * bloco melhor que uma seção: seção nasceria em todo relatório e ficaria em
 * branco — e relatório com seção em branco não se entrega.
 */
function AnexoNotarial({ casoId, chave }: { casoId: string; chave: string }) {
  const [anexos, setAnexos] = useState<
    Awaited<ReturnType<typeof apiCasosAtivos.anexoNotarial>>['anexos'] | null
  >(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const r = await apiCasosAtivos.anexoNotarial(casoId);
        if (vivo) setAnexos(r.anexos);
      } catch {
        /* Silêncio: a ausência de anexo é o estado normal da maioria dos casos. */
      }
    })();
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  if (!anexos || anexos.length === 0) return null;

  return (
    <div className="anexo-notarial">
      {anexos.map((a) => (
        <div key={a.investigado}>
          <p className="rotulo">Anexo · Atos notariais — {a.investigado}</p>
          <p className="ajuda">{a.abertura}</p>
          <div className="anexo-notarial-tabela">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tabelionato</th>
                  <th>Município</th>
                  <th>Espécie</th>
                  <th>Livro</th>
                  <th>Folha</th>
                  <th>Cópia</th>
                </tr>
              </thead>
              <tbody>
                {a.linhas.map((l) => (
                  <tr key={`${l.ocorridoEm}-${l.livro}-${l.folha}-${l.cartorio}`}>
                    <td className="dado">{paraTela(l.ocorridoEm)}</td>
                    <td>{l.cartorio}</td>
                    <td>
                      {l.municipio}/{l.uf}
                    </td>
                    <td>{l.especie === 'escritura' ? 'Escritura' : 'Procuração'}</td>
                    <td className="dado">{l.livro}</td>
                    <td className="dado">{l.folha}</td>
                    <td className={l.extraido ? 'anexo-examinado' : ''}>
                      {l.extraido ? 'sim' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Um campo que grava ao sair, e diz que gravou.
 *
 * ⚠️ **Só grava quando o texto MUDOU.** Sem essa conferência, passar o cursor
 * por dez seções gravaria as dez — dez escritas no banco e dez avisos de
 * "gravado" para quem não escreveu nada. Aviso que aparece sem motivo é aviso
 * que se aprende a ignorar.
 */
function Campo({
  rotulo,
  ajuda,
  natureza,
  valor,
  travado,
  rotuloOculto = false,
  aoGravar,
}: {
  rotulo: string;
  ajuda?: string;
  natureza?: 'fato' | 'achado' | 'recomendacao' | 'ressalva';
  valor: string;
  travado: boolean;
  /**
   * ⚠️ **Some com o texto do rótulo, nunca com o `<label>` — 20/08/2026.**
   *
   * Dentro de uma seção retrátil o título já está no cabeçalho, e repeti-lo no
   * campo faria "INVESTIGADOS" aparecer duas vezes na mesma tela. Mas o
   * `<label>` continua ligado ao `<textarea>`: quem usa leitor de tela precisa
   * ouvir o nome do campo em que está escrevendo, e um `textarea` sem rótulo é
   * uma caixa anônima. O que sai é a repetição visual, não a etiqueta.
   *
   * **O "gravando…" continua aparecendo**, porque é o único retorno de que o
   * texto foi salvo — e gravação silenciosa não tranquiliza ninguém.
   */
  rotuloOculto?: boolean;
  aoGravar: (texto: string) => Promise<void>;
}) {
  const [texto, setTexto] = useState(valor);
  const [estado, setEstado] = useState<'parado' | 'gravando' | 'gravado'>('parado');
  const original = useRef(valor);

  useEffect(() => {
    setTexto(valor);
    original.current = valor;
  }, [valor]);

  async function sair() {
    if (travado || texto === original.current) return;
    setEstado('gravando');
    try {
      await aoGravar(texto);
      original.current = texto;
      setEstado('gravado');
    } catch (e) {
      setEstado('parado');
      // A mensagem do servidor já diz o motivo — inclusive "já foi entregue".
      avisarErro(e);
    }
  }

  return (
    <div className="campo">
      <label className={'rotulo' + (rotuloOculto ? ' rotulo-so-do-estado' : '')} htmlFor={`sec-${rotulo}`}>
        <span className={rotuloOculto ? 'so-leitor-de-tela' : undefined}>{rotulo}</span>
        {/* ⚠️ A natureza aparece porque muda o que se escreve ali: fato pode
            vir de consulta, achado e recomendação são leitura de gente. */}
        {natureza && natureza !== 'fato' && (
          <span className={`selo-natureza selo-${natureza}`}>
            {natureza === 'achado' ? 'análise' : 'recomendação'}
          </span>
        )}
        {estado === 'gravando' && <span className="fantasia"> · gravando…</span>}
        {estado === 'gravado' && <span className="fantasia"> · gravado</span>}
      </label>
      <textarea
        id={`sec-${rotulo}`}
        rows={6}
        value={texto}
        disabled={travado}
        onChange={(e) => {
          setTexto(e.target.value);
          if (estado === 'gravado') setEstado('parado');
        }}
        onBlur={() => void sair()}
      />
      {ajuda && <p className="ajuda">{ajuda}</p>}
    </div>
  );
}

/**
 * ⭐⭐ **A COBRANÇA DA APURAÇÃO, por tópico — 24/09/2026, pedido da Karen.**
 *
 * Diz uma de duas coisas, e nunca as duas:
 *
 * ⭐ **Autorizado:** *"se nada for lançado aqui, o documento escreve sozinho
 * que nada foi localizado"* — para ninguém escrever à mão o que o Hub já diz,
 * que foi o que aconteceu no tópico Imóveis do caso 2026-003.
 *
 * ⛔ **Não autorizado:** o que falta, com número. Sem isto, o tópico ficava
 * mudo e ninguém sabia se era *"não achamos"* ou *"não procuramos"*.
 *
 * ⚠️ **Não trava nada.** É a mesma regra da trilha de onde sai: conta, nunca
 * age. Quem decide escrever é quem assina.
 */
function ApuracaoDoTopicoNaTela({
  chave,
  apuracao,
  escopos,
}: {
  chave: string;
  apuracao: ApuracaoDoTopico[];
  escopos: Array<{ chave: string; frase: string | null }>;
}) {
  const a = apuracao.find((x) => x.chave === chave);
  /*
   * ⭐ **A frase de escopo tem precedência sobre a de ausência**, e nunca as
   * duas: se há achado, o tópico não vai afirmar que nada foi localizado.
   */
  const escopo = escopos.find((x) => x.chave === chave)?.frase;
  if (escopo) {
    return (
      <p className="ajuda apuracao-ok">
        <Icone nome="concluido" tamanho={13} />
        <span>
          A apuração alcançou todas as partes. O documento escreve sozinho, no alto deste
          tópico: <em>{escopo}</em>
        </span>
      </p>
    );
  }
  if (!a) return null;
  if (a.autoriza) {
    return (
      <p className="ajuda apuracao-ok">
        <Icone nome="concluido" tamanho={13} /> A apuração deste tópico respondeu. Se nada for
        lançado aqui, o documento escreve sozinho que nada foi localizado — não é preciso
        escrever à mão.
      </p>
    );
  }
  if (!a.porque) return null;
  /*
   * ⛔ **O prefixo só vale para quem depende de consulta — 24/09/2026.**
   *
   * No grupo econômico a apuração TERMINOU: o que impede o documento de negar
   * é o Hub ter achado coincidência. Dizer ali "a apuração não terminou"
   * inverte o fato, e é a segunda frase do mesmo parágrafo que o desmente.
   */
  return (
    <p className="ajuda-alerta ajuda-solta apuracao-falta">
      {a.dependeDeConsulta && <strong>A apuração deste tópico não terminou. </strong>}
      {a.porque}
    </p>
  );
}
